import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getOrderById, mapOrderToListView } from '@/hooks/useOrder';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Route, Routes, useParams, Navigate, useNavigate, useLocation } from 'react-router-dom';
import update from 'immutability-helper';
import PropTypes from 'prop-types';

import ActionsMenu from './ActionsMenu';
import API from './apiRequest';
import AddPerformingInspector from './components/AddPerformingInspector';
import AssignInspector from './components/AssignInspector';
import Modal from './components/common/Modal';
import ChecklistForm from './components/ExtraActions/ChecklistForm';
import ExtraActions from './components/ExtraActions';
import RepairBudgetForm from './components/ExtraActions/RepairBudgetForm';
import OrderCancelPage from './components/OrderCancelPage';
import OrderIcon from './components/OrderIcon/OrderIcon';
import OrderOptionsList from './components/OrderOptionList/OrderOptionsList';
import OrdersReportsModal from './components/OrdersReportsModal';
import MarketingModal from './components/MarketingModal';
import { getCookie, setCookie } from 'typescript-cookie';
import UserContext from './contexts/UserContext';
import {
  ITEMS_PER_PAGE,
} from './constants';
import ApiContext from './contexts/ApiContext';
import { AsyncServiceContext } from './contexts/AsyncServiceContext';
import Empty from './Empty';
import ErrorPage from './ErrorPage';
import Header from './Header';
import getDashboardLink from './helpers/get-dashboard-link';
import getDivergencesPanelLink from './helpers/get-divergences-panel-link';
import InspectionAttach from './inspectionAttach/InspectionAttach';
import LeftDrawer from './leftDrawer/LeftDrawer';
import Loading from './Loading';
import OrderDetails from './orderDetails/OrderDetails';
import EditOrderFormPage from './orderForm/OrderForm/OrderFormPage/EditOrderFormPage';
import OrderFormPage from './orderForm/OrderForm/OrderFormPage/OrderFormPage';
import OrderSelectionMultiple from './OrderSelectionMultiple';
import OrdersList from './OrdersList';
import { setQuery } from './store/ducks/filter';
import { showToast } from './store/ducks/toasts';
import { useOrderView } from './contexts/OrderViewContext';

// Update this date when a new marketing campaign is published.
export const CONTESTATIONS_MARKETING_PUBLISHED_AT = '2026-10-07T00:00:00-03:00';
export const CONTESTATIONS_MARKETING_DURATION_DAYS = 30;
export const CONTESTATIONS_MARKETING_COOKIE_EXPIRES_DAYS = 2;

const newElement = {
  id: null,
  identifier: 'identifier',
  type: 'type',
  code: 'Code',
  status: 'WAITING',
  credits: null,
  express: null,
  premium: null,
  building_type: 'building_type',
  created_at: new Date(),
  owner: 'owner',
  client: 'client',
};

const status = new Map([
  ['waiting', 'waiting'],
  ['accepted', 'accepted'],
  ['assigned', 'assigned'],
  ['in_progress', 'in_progress'],
  ['finalized', 'finalized'],
  ['archived', 'archived'],
  ['vnr', 'vnr'],
  ['sent', 'sent'],
  ['rejected', 'rejected'],
  ['canceled', 'canceled'],
  ['independent', 'independent'],
]);

const currentPageByCurrentMenu = new Map([
  ['active', 'active'],
  ['waiting', 'active'],
  ['accepted', 'active'],
  ['assigned', 'active'],
  ['in_progress', 'active'],
  ['finalized', 'active'],
  ['vnr', 'active'],
  ['archived', 'archived'],
  ['sent', 'sent'],
  ['rejected', 'rejected'],
  ['canceled', 'canceled'],
  ['independent', 'independent'],
]);

const orderStatusToStatusLabel = new Map([
  ['WAITING', 'waiting'],
  ['ACCEPTED', 'accepted'],
  ['ASSIGNED', 'assigned'],
  ['IN_PROGRESS', 'in_progress'],
  ['FINALIZED_BY_DEVICE', 'finalized'],
  ['VNR', 'vnr'],
  ['ARCHIVED', 'archived'],
  ['FINISHED', 'sent'],
  ['REJECTED', 'rejected'],
  ['CANCELED', 'canceled'],
  ['FINALIZED', 'independent'],
]);

const getPreviousInspectionAttachConfig = (orderType, intl) => {
  let previousOrderType = null;
  let header = null;
  let previousType = null;

  if (orderType === 'saida') {
    previousOrderType = "['entrada','aditivo']";
    header = 'inspection.saida';
    previousType = intl.formatMessage({ id: 'entradaOrAditivo' });
  } else if (orderType === 'saidaCurtaTemporada') {
    previousOrderType = "['entradaCurtaTemporada']";
    header = 'inspection.saidaCurtaTemporada';
    previousType = intl.formatMessage({ id: 'entradaCurtaTemporada' });
  } else if (orderType === 'conferencia') {
    previousOrderType = "['saida','conferencia']";
    header = 'inspection.conferencia';
    previousType = intl.formatMessage({ id: 'saidaOrConferencia' });
  } else if (orderType === 'conferenciaFinalDeObra') {
    previousOrderType = "['finalDeObra','conferenciaFinalDeObra']";
    header = 'inspection.conferenciaFinalDeObra';
    previousType = intl.formatMessage({ id: 'finalDeObraOrConferenciaFinalDeObra' });
  } else if (orderType === 'conferenciaEntrega') {
    previousOrderType = "['entrega','conferenciaEntrega']";
    header = 'inspection.conferenciaEntrega';
    previousType = intl.formatMessage({ id: 'entregaOrConferenciaEntrega' });
  } else if (orderType === 'aditivo') {
    previousOrderType = "['entrada', 'aditivo']";
    header = 'inspection.aditivo';
    previousType = intl.formatMessage({ id: 'entradaOrAditivo' });
  } else if (orderType === 'revistoriaBrookfield') {
    previousOrderType = "['brookfield', 'revistoriaBrookfield']";
    header = 'inspection.brookfield';
    previousType = intl.formatMessage({ id: 'brookfieldOrRevistoria' });
  } else if (orderType === 'acompanhamentoObraMM') {
    previousOrderType = 'acompanhamentoObraMM';
    header = 'inspection.acompanhamentoObraMM';
    previousType = intl.formatMessage({ id: previousOrderType });
  } else if (orderType === 'acompanhamentoUrba') {
    previousOrderType = 'acompanhamentoUrba';
    header = 'inspection.acompanhamentoUrba';
    previousType = intl.formatMessage({ id: previousOrderType });
  } else if (orderType === 'auditoriaCbre') {
    previousOrderType = 'auditoriaCbre';
    header = 'inspection.auditoriaCbre';
    previousType = intl.formatMessage({ id: previousOrderType });
  } else if (orderType === 'intermediariaBHomy') {
    previousOrderType = "['entradaBHomy','intermediariaBHomy']";
    header = 'inspection.intermediariaBHomy';
    previousType = intl.formatMessage({ id: 'entradaBHomyOrIntermediariaBHomy' });
  }

  return {
    previousOrderType,
    header,
    previousType,
    isAcompanhamentoType: orderType === 'acompanhamentoObraMM' || orderType === 'acompanhamentoUrba' || orderType === 'auditoriaCbre',
  };
};

const EditPreviousInspectionWrapper = ({
  editPreviousInspectionOrderId,
  editPreviousInspectionType,
  intl,
  editPreviousInspection,
}) => {
  const navigate = useNavigate();
  const { orderId } = useParams();

  if (
    !editPreviousInspectionOrderId ||
    !editPreviousInspectionType ||
    String(editPreviousInspectionOrderId) !== String(orderId)
  ) {
    navigate(`/orders/${orderId}`, { replace: true });
    return null;
  }

  const attachConfig = getPreviousInspectionAttachConfig(editPreviousInspectionType, intl);
  const { previousOrderType, header, previousType, isAcompanhamentoType } = attachConfig;

  if (!previousOrderType || !header || !previousType) {
    navigate(`/orders/${orderId}`, { replace: true });
    return null;
  }

  return (
    <InspectionAttach
      header={<FormattedMessage id={header} />}
      subHeader={
        <FormattedMessage
          id={isAcompanhamentoType ? 'inspectionAttach.acompanhamento' : 'inspectionAttach.entry'}
          values={{ type: previousType }}
        />
      }
      handleSearchConfirm={editPreviousInspection}
      searchPlaceholder={intl.formatMessage(
        { id: 'inspectionAttach.searchEntry' },
        { type: previousType.toLowerCase() }
      )}
      apiUrl={`/orders?type=${previousOrderType}&status=sent&search=`}
      previousType={previousType}
      newOrderType={editPreviousInspectionType}
      onlySearch
    />
  );
};

const AttachWrapper = ({ intl, orderAttachPanel, orderAttachFile, continueWithoutAttachment }) => {
  const { orderType } = useParams();
  const attachConfig = getPreviousInspectionAttachConfig(orderType, intl);
  const { previousOrderType, header, previousType, isAcompanhamentoType } = attachConfig;

  if (!orderType || !previousOrderType || !header || !previousType) {
    return <Navigate to="/orders/new" replace />;
  }

  return (
    <InspectionAttach
      header={<FormattedMessage id={header} />}
      subHeader={
        <FormattedMessage
          id={isAcompanhamentoType ? 'inspectionAttach.acompanhamento' : 'inspectionAttach.entry'}
          values={{ type: previousType }}
        />
      }
      handleSearchConfirm={orderAttachPanel}
      handleFileConfirm={orderAttachFile}
      handleContinueWithouAttachment={continueWithoutAttachment}
      searchPlaceholder={intl.formatMessage(
        { id: 'inspectionAttach.searchEntry' },
        { type: previousType.toLowerCase() }
      )}
      apiUrl={`/orders?type=${previousOrderType}&status=sent&search=`}
      previousType={previousType}
      newOrderType={orderType}
    />
  );
};

const NewOrderFormRoute = ({
  previousOrderFile,
  inspectionIcon,
  updateOrderItem,
  cancelNewOrder,
  previousOrderId,
  successfulOrderCreation,
}) => {
  const { orderType, previousId } = useParams();
  const effectivePreviousId = previousId || previousOrderId;
  return (
    <OrderFormPage
      previousOrderFile={previousOrderFile}
      inspectionIcon={inspectionIcon}
      newOrderType={orderType}
      updateOrderItem={updateOrderItem}
      cancelNewOrder={cancelNewOrder}
      previousOrderId={effectivePreviousId}
      successfulOrderCreation={successfulOrderCreation}
    />
  );
};

const MergeWrapper = ({
  intl,
  independentMerge,
  currentInspectionType,
  setActiveOrderId,
  setCurrentInspectionType,
  activeOrder,
}) => {
  const { inspectionId } = useParams();

  useEffect(() => {
    if (inspectionId) {
      setActiveOrderId(inspectionId);
      if (activeOrder && activeOrder.type && !currentInspectionType) {
        setCurrentInspectionType(activeOrder.type);
      }
    }
  }, [inspectionId, setActiveOrderId, activeOrder, currentInspectionType, setCurrentInspectionType]);

  return (
    <InspectionAttach
      header={<FormattedMessage id="inspection.independentMerge" />}
      subHeader={<FormattedMessage id="inspectionMerge.order" />}
      handleSearchConfirm={independentMerge}
      searchPlaceholder={intl.formatMessage({ id: 'orderAttach.searchPanel' })}
      apiUrl={`/orders?type=${currentInspectionType}&status=FINALIZED&search=`}
      onlySearch
      smallSubHeader
    />
  );
};

const currentPageByOrderStatus = new Map([
  ['WAITING', 'active'],
  ['ACCEPTED', 'active'],
  ['ASSIGNED', 'active'],
  ['IN_PROGRESS', 'active'],
  ['FINALIZED_BY_DEVICE', 'active'],
  ['VNR', 'active'],
  ['ARCHIVED', 'archived'],
  ['FINISHED', 'sent'],
  ['REJECTED', 'rejected'],
  ['CANCELED', 'canceled'],
  ['FINALIZED', 'independent'],
]);

const statusLabelToSubMenu = new Map([
  ['waiting', 'active'],
  ['accepted', 'active'],
  ['assigned', 'active'],
  ['in_progress', 'active'],
  ['finalized', 'active'],
  ['archived', 'active'],
  ['vnr', 'active'],
  ['sent', 'finished'],
  ['rejected', 'finished'],
  ['canceled', 'finished'],
  ['independent', 'independent'],
]);

const FranchiseeApp = props => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const params = useParams();
  const queryClient = useQueryClient();

  const {
    activeOrderId,
    setActiveOrderId,
    activeOrder,
    setActiveOrder,
    currentPage,
    setCurrentPage,
    selectedOrders,
    setSelectedOrders,
    selectOrder,
    clearSelection,
    orderStatus,
    setOrderStatus,
  } = useOrderView();

  const asyncService = useContext(AsyncServiceContext);

  const { features, franchiseeId, userName, ordersChanged, ordersUpdated } = props;

  const [orderBy, setOrderBy] = useState('created_at');
  const [orderByDir, setOrderByDir] = useState('desc');
  const [rightState, setRightState] = useState('empty');
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [newOrderType, setNewOrderType] = useState(null);
  const [previousOrderId, setPreviousOrderId] = useState(null);
  const [editPreviousInspectionOrderId, setEditPreviousInspectionOrderId] = useState(null);
  const [editPreviousInspectionType, setEditPreviousInspectionType] = useState(null);
  const [actionsMenuChecked, setActionsMenuChecked] = useState(false);
  const [previousOrderFile, setPreviousOrderFile] = useState(null);
  const [data, setData] = useState([]);
  const [activeSubMenu, setActiveSubMenu] = useState('active');
  const [activeSubMenuIndex, setActiveSubMenuIndex] = useState(0);
  const [activeCollapseSubMenu, setActiveCollapseSubMenu] = useState(null);
  const [headerSearchValue, setHeaderSearchValue] = useState('');
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [apiStatus, setApiStatus] = useState('IDLE');
  const [orderIdFromUrl, setOrderIdFromUrl] = useState(null);
  const [isDisplayingSearchResults, setIsDisplayingSearchResults] = useState(false);
  const [modals, setModals] = useState({ extract: false });
  const [headerSearchTags, setHeaderSearchTags] = useState(null);
  const [headerSearchType, setHeaderSearchType] = useState(null);
  const [headerSearchOrderStatus, setHeaderSearchOrderStatus] = useState(null);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [currentInspectionType, setCurrentInspectionType] = useState(null);
  const me = useContext(UserContext);
  const [showMarketingModal, setShowMarketingModal] = useState(false);

  const handleCloseMarketingModal = () => setShowMarketingModal(false);

  useEffect(() => {
    // Show the campaign for 30 days, at most once every 48 hours per user.
    const marketingCampaignStart = new Date(CONTESTATIONS_MARKETING_PUBLISHED_AT);
    const marketingCampaignEnd = new Date(marketingCampaignStart);
    marketingCampaignEnd.setDate(marketingCampaignEnd.getDate() + CONTESTATIONS_MARKETING_DURATION_DAYS);
    const now = new Date();

    if (
      now >= marketingCampaignStart &&
      now <= marketingCampaignEnd &&
      me?.authRole === 'ROLE_FRANCHISEE' &&
      me?.identifier &&
      me?.profile?.username
    ) {
      const marketingKey = ['marketing_contestations', me.identifier, me.profile.username].join('_');
      const displayedMarketing = getCookie(marketingKey);

      if (!displayedMarketing) {
        setCookie(marketingKey, true, { expires: CONTESTATIONS_MARKETING_COOKIE_EXPIRES_DAYS });
        setShowMarketingModal(true);
      }
    }
  }, [me, franchiseeId]);

  useEffect(() => {
    dispatch(setQuery({ text: headerSearchValue || '' }));
  }, [headerSearchValue, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setQuery({}));
    };
  }, [dispatch]);

  const searchStateRef = useRef({
    value: '',
    tags: null,
    type: null,
    status: null,
  });

  const [menu, setMenu] = useState([
    {
      key: 'extract',
      active: false,
      requiredFeature: 'ordersReports',
      onClick: () => setModals(prev => ({ ...prev, extract: !prev.extract })),
    },
    {
      key: 'schedule',
      active: false,
      routeLink: '/schedule',
      requiredFeature: 'schedule',
      subMenu: [],
    },
    {
      key: 'clients',
      active: false,
      routeLink: '/clients',
      requiredFeature: 'client',
      subMenu: [
        { key: 'actives', active: true, content: 0 },
        { key: 'deleted', active: false, content: 0 },
      ],
    },
    {
      key: 'divergences',
      active: false,
      routeLink: '/divergences',
      requiredFeature: 'divergencePanel',
      onClick: getDivergencesPanelLink,
    },
    {
      key: 'dashboard',
      active: false,
      routeLink: '/dashboard',
      onClick: getDashboardLink,
    },
    {
      key: 'inspectors',
      active: false,
      routeLink: '/inspectors',
      requiredFeature: 'inspector',
      subMenu: [
        { key: 'active', active: true, content: 0 },
        { key: 'deleted', active: false, content: 0 },
      ],
    },
    {
      key: 'settings',
      active: false,
      routeLink: '/settings',
      requiredFeature: 'user',
      subMenu: [],
    },
    {
      key: 'inspections',
      active: true,
      subMenu: [
        {
          key: 'active',
          active: true,
          disabled: false,
          content: 0,
          routeLink: null,
          collapseSubMenu: [
            { key: 'waiting', active: true, disabled: false, content: 0, routeLink: '/orders?tab=waiting' },
            { key: 'accepted', active: false, disabled: false, content: 0, routeLink: '/orders?tab=accepted' },
            { key: 'assigned', active: false, disabled: false, content: 0, routeLink: '/orders?tab=assigned' },
            { key: 'in_progress', active: false, disabled: false, content: 0, routeLink: '/orders?tab=in_progress' },
            { key: 'finalized', active: false, disabled: false, content: 0, routeLink: '/orders?tab=finalized' },
            { key: 'archived', active: false, disabled: false, content: 0, routeLink: '/orders?tab=archived' },
            { key: 'vnr', active: false, disabled: false, content: 0, routeLink: '/orders?tab=vnr' },
          ],
        },
        {
          key: 'finished',
          active: false,
          disabled: false,
          content: 0,
          routeLink: null,
          collapseSubMenu: [
            { key: 'sent', active: false, disabled: false, content: 0, routeLink: '/orders?tab=sent' },
            { key: 'rejected', active: false, disabled: false, content: 0, routeLink: '/orders?tab=rejected' },
            { key: 'canceled', active: false, disabled: false, content: 0, routeLink: '/orders?tab=canceled' },
          ],
        },
        { key: 'independent', active: false, disabled: false, content: 0, routeLink: '/inspections' },
      ],
    },
  ]);

  const timer = useRef(null);
  const isUpdatingPreviousInspection = useRef(false);
  const source = useRef(null);
  const isLoadingMore = useRef(false);

  const deduplicateOrders = orders => {
    const seen = new Set();
    return (orders || []).filter(order => {
      if (!order) return false;
      if (!order.id) return true; // Keep null placeholders separate (they use index as key fallback)
      const idStr = String(order.id);
      if (seen.has(idStr)) return false;
      seen.add(idStr);
      return true;
    });
  };

  const setDefaltOrderBy = activeSubMenuValue => {
    let dir;
    switch (activeSubMenuValue) {
      case 'sent':
      case 'rejected':
      case 'canceled':
      case 'independent':
        dir = 'desc';
        break;
      default:
        dir = 'asc';
        break;
    }
    setOrderByDir(dir);
    return dir;
  };

  const handleOrdersRequestByStatus = useCallback(
    async (orderStatus = 'waiting', overrideOrderByDir) => {
      dispatch(setQuery({}));
      setLoadingOrders(true);

      try {
        if (!orderStatus) throw new Error();

        const effectiveOrderByDir = overrideOrderByDir || orderByDir;

        const ordersByStatus = await API.get(
          `/orders?status=${orderStatus}&limit=${ITEMS_PER_PAGE}&order_by=${orderBy}&order_by_dir=${effectiveOrderByDir}`
        );

        setData(deduplicateOrders(ordersByStatus?.data || []));
        setLoadingOrders(false);
        setHasMoreItems(ordersByStatus?.data?.length === ITEMS_PER_PAGE);
      } catch (err) {
        console.debug('FranchiseeApp.handleOrdersRequestByStatus error', err);
        setLoadingOrders(false);
        setHasMoreItems(false);
      }
    },
    [dispatch, orderBy, orderByDir]
  );

  const triggerSearch = useCallback(
    overrideOrderByDir => {
      setLoadingOrders(true);
      setRightState('empty');
      setActiveOrderId(null);
      setNewOrderType(null);
      setPreviousOrderId(null);
      clearSelection();
      setActionsMenuChecked(false);
      setHasMoreItems(true);
      setPreviousOrderFile(null);
      setIsDisplayingSearchResults(true);

      let url = '/orders';
      const params = {
        limit: ITEMS_PER_PAGE,
        order_by: orderBy,
        order_by_dir: overrideOrderByDir || orderByDir,
      };

      if (activeSubMenu === 'independent') {
        url = '/inspections';
        params.status = 'independent';
      }

      const searchVal = searchStateRef.current.value;
      const searchTags = searchStateRef.current.tags;
      const searchType = searchStateRef.current.type;
      const searchStatus = searchStateRef.current.status;

      if (searchVal !== '') params.search = searchVal;
      if (searchTags) params.tags = searchTags;
      if (searchType) params.type = searchType;
      if (searchStatus) params.statusLabel = searchStatus;

      if (source.current !== null) source.current.cancel();

      source.current = API.CancelToken.source();

      API.get(url, { params, cancelToken: source.current.token }).then(res => {
        setLoadingOrders(false);
        setData(deduplicateOrders(res.data || []));
      });
    },
    [orderBy, orderByDir, activeSubMenu]
  );

  const refreshData = useCallback(
    overrideOrderByDir => {
      if (rightState === 'orderForm') {
        const confMessage = intl.formatMessage({ id: 'confirmation.cancelNewOrder' });
        if (window.confirm(confMessage)) {
          setData(prev => update(prev, { $splice: [[0, 1]] }));
          setRightState('empty');
          clearSelection();
          setPreviousOrderFile(null);
          setPreviousOrderId(null);
          setLoadingOrders(true);
          setIsDisplayingSearchResults(false);
        }
      } else if (rightState === 'orderOptions' || rightState === 'orderAttach' || rightState === 'independentMerge') {
        setData(prev => update(prev, { $splice: [[0, 1]] }));
        setRightState('empty');
        clearSelection();
        setLoadingOrders(true);
        setIsDisplayingSearchResults(false);
      } else if (rightState === 'orderEdit') {
        const confMessage = intl.formatMessage({ id: 'confirmation.cancelEditOrder' });
        if (window.confirm(confMessage)) {
          setRightState('empty');
          setActiveOrderId(null);
          setOrderStatus(null);
          clearSelection();
          setLoadingOrders(true);
          setIsDisplayingSearchResults(false);
        }
      } else {
        clearSelection();
        setLoadingOrders(true);
        setIsDisplayingSearchResults(false);
      }

      const effectiveOrderByDir = overrideOrderByDir || orderByDir;

      if (activeSubMenu === 'independent') {
        API.get(
          `/inspections?status=independent&limit=${ITEMS_PER_PAGE}&order_by=${orderBy}&order_by_dir=${effectiveOrderByDir}`
        ).then(res => {
          setData(deduplicateOrders(res.data || []));
          setLoadingOrders(false);
        });
      } else {
        handleOrdersRequestByStatus(activeSubMenu, overrideOrderByDir);
      }
    },
    [rightState, intl, activeSubMenu, orderBy, orderByDir, handleOrdersRequestByStatus]
  );

  const decrementBadge = useCallback(() => {
    const activeMenuOption = menu.find(option => option.active === true);
    const activeMenuIndex = menu.indexOf(activeMenuOption);
    const activeSubMenuOption = menu[activeMenuIndex].subMenu.find(option => option.active === true);
    const activeSubMenuIndex = menu[activeMenuIndex].subMenu.indexOf(activeSubMenuOption);
    const { content } = menu[activeMenuIndex].subMenu[activeSubMenuIndex];

    setMenu(prevMenu =>
      update(prevMenu, {
        [activeMenuIndex]: {
          subMenu: {
            [activeSubMenuIndex]: {
              content: { $set: content - 1 },
            },
          },
        },
      })
    );
  }, [menu]);

  const handleHeaderSearch = (searchString, tags, type, orderStatus) => {
    let confirm = true;

    if (rightState === 'orderForm') {
      const confMessage = intl.formatMessage({ id: 'confirmation.cancelNewOrder' });
      if (!window.confirm(confMessage)) confirm = false;
    } else if (rightState === 'orderEdit') {
      const confMessage = intl.formatMessage({ id: 'confirmation.cancelEditOrder' });
      if (!window.confirm(confMessage)) confirm = false;
    }

    if (confirm) {
      ordersUpdated();
      const activeMenuIndex = menu.findIndex(menuOption => menuOption.active === true);

      const updateMenuOptions = {
        subMenu: {
          0: {
            active: { $set: false },
            collapseSubMenu: {
              0: { active: { $set: false } },
              1: { active: { $set: false } },
              2: { active: { $set: false } },
              3: { active: { $set: false } },
              4: { active: { $set: false } },
              5: { active: { $set: false } },
              6: { active: { $set: false } },
            },
          },
          1: {
            active: { $set: false },
            collapseSubMenu: {
              0: { active: { $set: false } },
              1: { active: { $set: false } },
              2: { active: { $set: false } },
            },
          },
          2: { active: { $set: false } },
        },
      };

      setHeaderSearchValue(searchString);
      setHeaderSearchTags(tags);
      setHeaderSearchType(type);
      setHeaderSearchOrderStatus(orderStatus);
      searchStateRef.current = {
        value: searchString,
        tags,
        type,
        status: orderStatus,
      };
      setMenu(prev => update(prev, { [activeMenuIndex]: updateMenuOptions }));

      clearTimeout(timer.current);
      timer.current = setTimeout(triggerSearch, 500);
    }
  };

  const handleMenuChange = (index, e, link, routeLink) => {
    if (routeLink !== undefined) {
      navigate(routeLink);
      return;
    }

    if (link !== undefined) {
      window.location = `${window.location.protocol}//${window.location.hostname}:${window.location.port}${link}`;
      return;
    }
  };

  const handleSubMenuChange = async (newSubMenuIndex, e, routeLink) => {
    const activeMenuIndex = menu.findIndex(option => option.active === true);
    const activeSubMenuIndex = menu[activeMenuIndex].subMenu.findIndex(option => option.active === true);
    const newActiveSubMenu = menu[activeMenuIndex].subMenu[newSubMenuIndex].key;

    const defaultDir = setDefaltOrderBy(newActiveSubMenu);

    let confirm = true;

    if (rightState === 'orderForm') {
      const confMessage = intl.formatMessage({ id: 'confirmation.cancelNewOrder' });
      if (!window.confirm(confMessage)) confirm = false;
    } else if (rightState === 'orderEdit') {
      const confMessage = intl.formatMessage({ id: 'confirmation.cancelEditOrder' });
      if (!window.confirm(confMessage)) confirm = false;
    }

    const orderStatus = status.get(newActiveSubMenu);

    if (!orderStatus) {
      console.debug('FranchiseeApp.handleSubMenuChange error', `orderStatus not found for ${newActiveSubMenu}`);
      return;
    }

    let url = `/orders?status=${orderStatus}&limit=${ITEMS_PER_PAGE}&order_by=${orderBy}&order_by_dir=${
      defaultDir || orderByDir
    }`;

    if (newActiveSubMenu === 'independent') {
      url = `/inspections?status=${orderStatus}&limit=${ITEMS_PER_PAGE}&order_by=${orderBy}&order_by_dir=${
        defaultDir || orderByDir
      }`;
    }

    if (activeSubMenuIndex !== -1) {
      const activeSubMenuUpdateOptions = {
        [activeSubMenuIndex]: {
          active: { $set: false },
        },
      };

      if (Array.isArray(menu[activeMenuIndex].subMenu[activeSubMenuIndex].collapseSubMenu)) {
        const collapseSubMenuIndex = menu[activeMenuIndex].subMenu[activeSubMenuIndex].collapseSubMenu.findIndex(
          collapseSubMenuOption => collapseSubMenuOption.active === true
        );

        if (collapseSubMenuIndex !== -1) {
          activeSubMenuUpdateOptions[activeSubMenuIndex].collapseSubMenu = {
            [collapseSubMenuIndex]: {
              active: { $set: false },
            },
          };
        }
      }

      const newMenuOptions = update(menu, {
        [activeMenuIndex]: {
          subMenu: {
            ...activeSubMenuUpdateOptions,
            [newSubMenuIndex]: {
              active: { $set: true },
            },
          },
        },
      });

      if (confirm) {
        ordersUpdated();
        setLoadingOrders(true);
        setRightState('empty');
        setActiveOrderId(null);
        setOrderStatus(null);
        setNewOrderType(null);
        setPreviousOrderId(null);
        clearSelection();
        setActionsMenuChecked(false);
        setHasMoreItems(true);
        setPreviousOrderFile(null);
        setMenu(newMenuOptions);
        setActiveSubMenu(newActiveSubMenu);
        setHeaderSearchValue(null);
        setHeaderSearchTags(null);
        setHeaderSearchType(null);
        searchStateRef.current = { value: '', tags: null, type: null, status: null };
        setIsDisplayingSearchResults(false);
      }
    } else if (confirm) {
      const newMenuOptions = update(menu, {
        [activeMenuIndex]: {
          subMenu: {
            [newSubMenuIndex]: {
              active: { $set: true },
            },
          },
        },
      });

      ordersUpdated();

      setLoadingOrders(true);
      setRightState('empty');
      setActiveOrderId(null);
      setOrderStatus(null);
      setNewOrderType(null);
      setPreviousOrderId(null);
      clearSelection();
      setActionsMenuChecked(false);
      setHasMoreItems(true);
      setPreviousOrderFile(null);
      setMenu(newMenuOptions);
      setActiveSubMenu(newActiveSubMenu);
      setHeaderSearchValue(null);
      setHeaderSearchTags(null);
      setHeaderSearchType(null);
      searchStateRef.current = { value: '', tags: null, type: null, status: null };
      setIsDisplayingSearchResults(false);
    }

    if (routeLink) {
      API.get(url).then(res => {
        setLoadingOrders(false);
        setData(deduplicateOrders(res.data || []));
      });

      navigate(routeLink);
    }
  };

  const handleCollapseSubMenuChange = async (
    newSubMenuIndex = 0,
    newCollapseSubMenuIndex = 0,
    routeLink = '/orders?tab=waiting'
  ) => {
    const activeMenuIndex = menu.findIndex(menuOption => menuOption.active === true);
    const activeSubMenuIndex = menu[activeMenuIndex].subMenu.findIndex(subMenuOption => subMenuOption.active === true);
    let newActiveCollapseSubMenu = null;

    if (activeMenuIndex !== -1 && activeSubMenuIndex !== -1) {
      const updateMenuOptions = {
        subMenu: {
          0: {
            active: { $set: false },
            collapseSubMenu: {
              0: { active: { $set: false } },
              1: { active: { $set: false } },
              2: { active: { $set: false } },
              3: { active: { $set: false } },
              4: { active: { $set: false } },
              5: { active: { $set: false } },
              6: { active: { $set: false } },
            },
          },
          1: {
            active: { $set: false },
            collapseSubMenu: {
              0: { active: { $set: false } },
              1: { active: { $set: false } },
              2: { active: { $set: false } },
            },
          },
          2: { active: { $set: false } },
        },
      };

      Object.keys(updateMenuOptions.subMenu).forEach(subMenuIdx => {
        if (Number(subMenuIdx) === Number(newSubMenuIndex)) {
          updateMenuOptions.subMenu[subMenuIdx].active = { $set: true };

          if (updateMenuOptions.subMenu[subMenuIdx].collapseSubMenu) {
            Object.keys(updateMenuOptions.subMenu[subMenuIdx].collapseSubMenu).forEach(collapseSubMenuIdx => {
              if (Number(collapseSubMenuIdx) === Number(newCollapseSubMenuIndex)) {
                updateMenuOptions.subMenu[subMenuIdx].collapseSubMenu[collapseSubMenuIdx].active = { $set: true };

                const nextCollapseSubMenu =
                  menu[activeMenuIndex].subMenu[subMenuIdx].collapseSubMenu[collapseSubMenuIdx];

                if (nextCollapseSubMenu.key) {
                  newActiveCollapseSubMenu = nextCollapseSubMenu.key;
                }
              }
            });
          }
        }
      });

      const newOrderStatus = routeLink.substr(routeLink.lastIndexOf('=') + 1, routeLink.length);
      const defaultDir = setDefaltOrderBy(newOrderStatus);

      setMenu(prev => update(prev, { [activeMenuIndex]: updateMenuOptions }));
      setActiveSubMenu(newOrderStatus);
      setActiveCollapseSubMenu(newActiveCollapseSubMenu);
      setIsDisplayingSearchResults(false);
      setHasMoreItems(true);
      setHeaderSearchValue(null);
      setHeaderSearchTags(null);
      setHeaderSearchType(null);
      searchStateRef.current = { value: '', tags: null, type: null, status: null };

      navigate(routeLink);

      await handleOrdersRequestByStatus(newOrderStatus, defaultDir);
    } else {
      const updateMenuOptions = {
        subMenu: {
          [newSubMenuIndex]: {
            active: { $set: true },
            collapseSubMenu: {
              [newCollapseSubMenuIndex]: {
                active: { $set: true },
              },
            },
          },
        },
      };

      const nextCollapseSubMenu =
        menu[activeMenuIndex].subMenu[newSubMenuIndex].collapseSubMenu[newCollapseSubMenuIndex];

      if (nextCollapseSubMenu.key) {
        newActiveCollapseSubMenu = nextCollapseSubMenu.key;
      }

      const newOrderStatus = routeLink.substr(routeLink.lastIndexOf('=') + 1, routeLink.length);
      const defaultDir = setDefaltOrderBy(newOrderStatus);

      setMenu(prev => update(prev, { [activeMenuIndex]: updateMenuOptions }));
      setActiveSubMenu(newOrderStatus);
      setActiveCollapseSubMenu(newActiveCollapseSubMenu);
      setIsDisplayingSearchResults(false);
      setHeaderSearchValue(null);
      setHeaderSearchTags(null);
      setHeaderSearchType(null);
      searchStateRef.current = { value: '', tags: null, type: null, status: null };

      navigate(routeLink);

      await handleOrdersRequestByStatus(newOrderStatus, defaultDir);
    }
  };

  const orderShowDetailsAction = useCallback(
    orderIndex => {
      const order = data[orderIndex];
      if (!order) return;
      const orderId = order.id;
      const statusVal = order.status;
      setActiveOrder(order);

      const targetPath = activeSubMenu !== 'independent' ? `/orders/${orderId}` : `/inspections/${orderId}`;

      if (rightState === 'orderOptions' || rightState === 'orderAttach') {
        setData(prev => update(prev, { $splice: [[0, 1]] }));
        setRightState('orderDetails');
        setActiveOrderId(orderId);
        setOrderStatus(statusVal);
        setOrderIdFromUrl(null);
      } else if (rightState === 'orderForm') {
        const confMessage = intl.formatMessage({ id: 'confirmation.cancelNewOrder' });
        if (window.confirm(confMessage)) {
          setData(prev => update(prev, { $splice: [[0, 1]] }));
          setRightState('orderDetails');
          setActiveOrderId(orderId);
          setOrderStatus(statusVal);
          setPreviousOrderId(null);
          setPreviousOrderFile(null);
          setOrderIdFromUrl(null);
        }
      } else if (rightState === 'orderEdit') {
        const confMessage = intl.formatMessage({ id: 'confirmation.cancelEditOrder' });
        if (window.confirm(confMessage)) {
          setRightState('orderDetails');
          setActiveOrderId(orderId);
          setOrderStatus(statusVal);
          setOrderIdFromUrl(null);
        }
      } else {
        setRightState('orderDetails');
        setActiveOrderId(orderId);
        setOrderStatus(statusVal);
        clearSelection();
        setActionsMenuChecked(false);
        setOrderIdFromUrl(null);
      }

      if (location.pathname !== targetPath) {
        navigate(targetPath);
      }
    },
    [
      data,
      rightState,
      intl,
      activeSubMenu,
      navigate,
      setActiveOrderId,
      setActiveOrder,
      setOrderStatus,
      clearSelection,
      location.pathname,
    ]
  );

  const actionsMenuCheckAllAction = (e, action) => {
    let newSelected;

    if (action === 'check') {
      newSelected = new Set(data.filter(order => order?.id).map(order => order.id));
    } else {
      newSelected = new Set();
    }

    const nextRightState = action === 'check' && data.length > 0 ? 'orderSelection' : 'empty';

    if (nextRightState === 'orderSelection' && location.pathname !== '/orders/selection') {
      navigate('/orders/selection');
    } else if (nextRightState === 'empty' && location.pathname !== '/orders') {
      navigate('/orders');
    }

    setActionsMenuChecked(action === 'check');
    setSelectedOrders(newSelected);
    setRightState(nextRightState);
    setNewOrderType(null);
    setActiveOrderId(null);
    setOrderStatus(null);
    setPreviousOrderFile(null);
  };

  const checkOneItemAction = (id, evt) => {
    evt.stopPropagation();
    const newSelectedOrders = new Set(selectedOrders);

    if (selectedOrders.has(id)) {
      newSelectedOrders.delete(id);
    } else {
      newSelectedOrders.add(id);
    }

    if (rightState === 'orderForm') {
      const confMessage = intl.formatMessage({ id: 'confirmation.cancelNewOrder' });
      if (window.confirm(confMessage)) {
        setData(prev => update(prev, { $splice: [[0, 1]] }));
        setRightState('orderSelection');
        setSelectedOrders(newSelectedOrders);
        setPreviousOrderFile(null);
        setPreviousOrderId(null);
        navigate('/orders/selection');
      }
    } else if (rightState === 'orderOptions' || rightState === 'orderAttach') {
      setData(prev => update(prev, { $splice: [[0, 1]] }));
      setRightState('orderSelection');
      setSelectedOrders(newSelectedOrders);
      navigate('/orders/selection');
    } else if (rightState === 'orderEdit') {
      const confMessage = intl.formatMessage({ id: 'confirmation.cancelEditOrder' });
      if (window.confirm(confMessage)) {
        setRightState('orderSelection');
        setActiveOrderId(null);
        setOrderStatus(null);
        setSelectedOrders(newSelectedOrders);
        navigate('/orders/selection');
      }
    } else {
      const nextRightState = newSelectedOrders.size > 0 ? 'orderSelection' : 'empty';

      setRightState(nextRightState);
      setActiveOrderId(null);
      setOrderStatus(null);
      setSelectedOrders(newSelectedOrders);

      if (nextRightState === 'orderSelection') {
        if (location.pathname !== '/orders/selection') navigate('/orders/selection');
      } else {
        if (location.pathname !== '/orders') navigate('/orders');
      }
    }
  };

  const actionsMenuNewOrder = () => {
    if (rightState === 'orderForm') {
      const confMessage = intl.formatMessage({ id: 'confirmation.cancelNewOrder' });
      if (window.confirm(confMessage)) {
        setData(prev => update(prev, { 0: { $set: newElement } }));
        setRightState('orderOptions');
        setPreviousOrderFile(null);
        setPreviousOrderId(null);
        setNewOrderType(null);
      }
    } else if (rightState === 'orderOptions' || rightState === 'orderAttach') {
      setData(prev => update(prev, { 0: { $set: newElement } }));
      setRightState('orderOptions');
      setNewOrderType(null);
    } else if (rightState === 'orderEdit') {
      const confMessage = intl.formatMessage({ id: 'confirmation.cancelEditOrder' });
      if (window.confirm(confMessage)) {
        setData(prev => update(prev, { $unshift: [newElement] }));
        setRightState('orderOptions');
        setActiveOrderId(null);
        setOrderStatus(null);
      }
    } else {
      setData(prev => update(prev, { $unshift: [newElement] }));
      setActionsMenuChecked(false);
      setActiveOrderId(null);
      setOrderStatus(null);
      setRightState('orderOptions');
      clearSelection();
    }
    if (location.pathname !== '/orders/new') navigate('/orders/new');
  };

  const actionsMenuEditOrder = useCallback(() => {
    let orderId = activeOrderId;
    if (rightState === 'orderSelection') {
      orderId = selectedOrders.values().next().value;
      setRightState('orderEdit');
      setActiveOrderId(orderId);
      setOrderStatus(null);
      clearSelection();
    } else {
      setRightState('orderEdit');
    }
    const targetPath = `/orders/${orderId}/edit`;
    if (location.pathname !== targetPath) navigate(targetPath);
  }, [
    activeOrderId,
    rightState,
    selectedOrders,
    navigate,
    setActiveOrderId,
    setOrderStatus,
    clearSelection,
    location.pathname,
  ]);

  const actionsMenuOrderDelete = () => {
    let orderId = activeOrderId || selectedOrders.values().next().value;
    const activeIndex = data.findIndex(order => order && order.id === orderId);
    API.delete(`orders/${orderId}`)
      .then(() => {
        setData(prev => update(prev, { $splice: [[activeIndex, 1]] }));
        setRightState('empty');
        setActiveOrderId(null);
        setOrderStatus(null);
        clearSelection();
        if (location.pathname !== '/orders') navigate('/orders');
      })
      .catch(error => console.log(error));
  };

  const actionsMenuOrderCancel = () => {
    setRightState('orderCancel');
    if (location.pathname !== '/orders/cancel') navigate('/orders/cancel');
  };

  const cancelOrder = orderId => {
    const activeIndex = data.findIndex(order => order && order.id === orderId);
    setData(prev => update(prev, { $splice: [[activeIndex, 1]] }));
    setRightState('empty');
    setActiveOrderId(null);
    setOrderStatus(null);
    clearSelection();
    if (location.pathname !== '/orders') navigate('/orders');
  };

  const actionsMenuGeneratePDF = (withPictures, type, label, inspectionId) => {
    let entityType = inspectionId !== undefined ? 'inspections' : 'orders';
    let entityId = null;
    if (entityType === 'inspections') {
      entityId = inspectionId;
    } else if (activeOrderId !== null) {
      entityId = activeOrderId;
    } else {
      entityId = selectedOrders.values().next().value;
    }
    if (activeSubMenu === 'independent') entityType = 'inspections';

    API.post(`${entityType}/${entityId}/report`, { withPictures, type, label })
      .then(res => {
        if (res.data?.flag === 'cache') return;
        asyncService.showReportLoadingToast({
          label: label || '',
          requestId: res.data?.request_id,
          withPictures: !!withPictures,
        });
      })
      .catch(error => console.log(error));
  };

  const actionsMenuGeneratePhotos = () => {
    let entityId = activeOrderId || selectedOrders.values().next().value;
    const isInspection = !Number.isNaN(parseFloat(entityId)) && Number.isFinite(entityId);
    const entityType = isInspection ? 'inspections' : 'orders';
    API.post(`${entityType}/${entityId}/photos`, {})
      .then(res => {
        asyncService.showReportLoadingToast({
          label: 'arquivo',
          requestId: res.data?.request_id,
          withPictures: true,
        });
      })
      .catch(error => console.log(error));
  };

  const actionsMenuOrderArchive = (e, action) => {
    let selectedOrdersList = [];
    if (selectedOrders.size > 0) {
      selectedOrdersList = Array.from(selectedOrders);
    } else if (activeOrderId !== null) {
      selectedOrdersList = [activeOrderId];
    } else {
      return;
    }

    API.post('batch/orders/archive', { action, orders: selectedOrdersList })
      .then(() => {
        const filteredOrders = data.filter(order => order && !selectedOrdersList.includes(order.id));
        setData(filteredOrders);
        setRightState('empty');
        setActiveOrderId(null);
        setOrderStatus(null);
        clearSelection();
        if (location.pathname !== '/orders') navigate('/orders');
      })
      .catch(error => console.log(error));
  };

  const handleOrderOptionSelect = option => {
    const newData = update(data, {
      0: { type: { $set: option }, formatted_type: { $set: option } },
    });
    const attachOptions = [
      'saida',
      'saidaCurtaTemporada',
      'conferencia',
      'aditivo',
      'revistoriaBrookfield',
      'acompanhamentoObraMM',
      'conferenciaFinalDeObra',
      'conferenciaEntrega',
      'intermediariaBHomy',
      'acompanhamentoUrba',
      'auditoriaCbre',
    ];

    setData(newData);
    if (attachOptions.includes(option)) {
      setRightState('orderAttach');
      setNewOrderType(option);
      navigate(`/orders/new/${option}/attach`);
    } else {
      setRightState('orderForm');
      setNewOrderType(option);
      navigate(`/orders/new/${option}/form`);
    }
  };

  const updateOrderItem = useCallback(
    newData => {
      const activeIndex = data.findIndex(order => order && String(order.id) === String(activeOrderId));
      if (activeIndex === -1) return;

      const item = { ...data[activeIndex] };
      item.id = newData.id;
      item.client = newData.client;
      item.code = newData.code;
      item.created_at = newData.created_at?.date || newData.created_at;
      item.credits = newData.credits;
      item.identifier = newData.identifier;
      item.status = newData.status === 'FINALIZED' ? 'FINALIZED_BY_DEVICE' : newData.status;
      item.type = newData.type;
      item.inspector = newData.inspector;

      setData(prev => update(prev, { [activeIndex]: { $set: item } }));
    },
    [data, activeOrderId]
  );

  const cancelNewOrder = () => {
    setData(prev => update(prev, { $splice: [[0, 1]] }));
    setRightState('empty');
    setNewOrderType(null);
    setActiveOrderId(null);
    setPreviousOrderId(null);
    setPreviousOrderFile(null);
    navigate('/orders');
  };

  const orderAttachPanel = option => {
    setRightState('orderForm');
    setPreviousOrderId(option.id);
    navigate(`/orders/new/${newOrderType}/form`);
  };

  const continueWithoutAttachment = () => {
    setRightState('orderForm');
    setPreviousOrderId(null);
    navigate(`/orders/new/${newOrderType}/form`);
  };

  const independentAttach = option => {
    const inspectionId = activeOrderId;
    const orderIdValue = option.id;
    API.put(`/inspections/${inspectionId}/order`, { order_id: orderIdValue })
      .then(() => {
        refreshData();
        navigate('/inspections');
      })
      .catch(error => console.log(error));
  };

  const independentMerge = option => {
    const inspectionId = activeOrderId;
    const orderIdValue = option.id;
    const orderCode = option.code;

    API.post(`/inspections/${inspectionId}/merge-to-order`, { order_id: orderIdValue })
      .then(() => {
        asyncService.showMergeLoadingToast({ orderId: orderIdValue, orderCode });
        refreshData();
        navigate('/inspections');
      })
      .catch(error => {
        console.error('Error merging inspection to order:', error);
        let errorMessage = intl.formatMessage({ id: 'MERGE_UNKNOWN_ERROR' });
        if (error.response?.data?.error) {
          const apiError = error.response.data.error;
          const errorMappings = {
            ORDER_STATUS_INVALID: 'MERGE_INSPECTION_ORDER_STATUS_INVALID',
            INSPECTION_NOT_FOUND: 'MERGE_INSPECTION_NOT_FOUND',
            ORDER_NOT_FOUND: 'MERGE_ORDER_NOT_FOUND',
            ORDER_HAS_NO_OFFICIAL_INSPECTION: 'MERGE_ORDER_HAS_NO_OFFICIAL_INSPECTION',
            PRIMARY_INSPECTION_INVALID: 'MERGE_PRIMARY_INSPECTION_INVALID',
            SECONDARY_INSPECTION_INVALID: 'MERGE_SECONDARY_INSPECTION_INVALID',
            INSPECTION_TYPE_MISMATCH: 'MERGE_INSPECTION_TYPE_MISMATCH',
            INSPECTION_FRANCHISEE_MISMATCH: 'MERGE_INSPECTION_FRANCHISEE_MISMATCH',
            SECONDARY_INSPECTION_HAS_ORDER: 'MERGE_SECONDARY_INSPECTION_HAS_ORDER',
            PRIMARY_DELETED: 'MERGE_INSPECTION_DELETED',
            SECONDARY_DELETED: 'MERGE_INSPECTION_DELETED',
            PRIMARY_NOT_AVAILABLE_ON_S3: 'MERGE_INSPECTION_NOT_AVAILABLE_ON_S3',
            SECONDARY_NOT_AVAILABLE_ON_S3: 'MERGE_INSPECTION_NOT_AVAILABLE_ON_S3',
            PRIMARY_PROGRESS_STATUS_INVALID: 'MERGE_INSPECTION_PROGRESS_STATUS_INVALID',
            SECONDARY_PROGRESS_STATUS_INVALID: 'MERGE_INSPECTION_PROGRESS_STATUS_INVALID',
          };
          const baseError = apiError.split(':')[0];
          const translationKey = errorMappings[baseError] || errorMappings[apiError];
          if (translationKey) errorMessage = intl.formatMessage({ id: translationKey });
        }
        dispatch(
          showToast({ type: 'error', title: intl.formatMessage({ id: 'ERROR' }), text: errorMessage, duration: 5000 })
        );
      });
  };

  const orderAttachFile = (fileExtension, originalName, remoteName) => {
    const previousOrderFileValue = {
      file: { original_name: originalName, remote_name: remoteName, extension: fileExtension },
    };
    setRightState('orderForm');
    setPreviousOrderFile(previousOrderFileValue);
    navigate(`/orders/new/${newOrderType}/form`);
  };

  const successfulOrderCreation = newData => {
    const createdOrder = mapOrderToListView(newData);
    if (createdOrder?.id) {
      setData(prev => {
        const existingOrders = prev.filter(
          order => order?.id != null && String(order.id) !== String(createdOrder.id)
        );
        return [createdOrder, ...existingOrders];
      });
    }
    setRightState('orderDetails');
    setActiveOrderId(createdOrder?.id ?? null);
    setActiveOrder(createdOrder);
    setOrderStatus(createdOrder?.status ?? null);
    setNewOrderType(null);
    setPreviousOrderId(null);
    if (createdOrder?.id) navigate(`/orders/${createdOrder.id}`);
  };

  const successfulOrderEdition = orderIdValue => {
    const targetOrderId = orderIdValue || params.orderId || null;
    setRightState('orderDetails');
    if (targetOrderId) navigate(`/orders/${targetOrderId}`);
    else navigate('/orders');
  };

  const cancelEditOrder = () => {
    setRightState('empty');
    setActiveOrderId(null);
    navigate('/orders');
  };

  const loadMoreItems = page => {
    if (!hasMoreItems || isLoadingMore.current) return;
    isLoadingMore.current = true;

    const offset = page * ITEMS_PER_PAGE;
    let url = '/orders';
    const paramsQuery = { limit: ITEMS_PER_PAGE, offset, order_by: orderBy, order_by_dir: orderByDir };
    if (!isDisplayingSearchResults || activeSubMenu === 'independent') paramsQuery.status = status.get(activeSubMenu);
    if (activeSubMenu === 'independent') url = '/inspections';
    if (headerSearchValue !== '') paramsQuery.search = headerSearchValue;
    if (headerSearchTags) paramsQuery.tags = headerSearchTags;
    if (headerSearchType) paramsQuery.type = headerSearchType;

    API.get(url, { params: paramsQuery })
      .then(res => {
        if (res.data?.length) {
          setData(prev => deduplicateOrders([...prev, ...res.data]));
          setHasMoreItems(res.data.length === ITEMS_PER_PAGE);
        } else {
          setHasMoreItems(false);
        }
      })
      .catch(err => console.error(err))
      .finally(() => {
        isLoadingMore.current = false;
      });
  };

  const rejectOrder = message => {
    const activeIdx = data.findIndex(order => order && String(order.id) === String(activeOrderId));
    setRequestInProgress(true);
    return API.post(`orders/${activeOrderId}/status`, { action: 'reject', message })
      .then(res => {
        if (res.data) {
          updateOrderItem(res.data);
        }
        queryClient.invalidateQueries({ queryKey: ['order', activeOrderId] });
      })
      .catch(error => {
        console.log(error);
        throw error;
      })
      .finally(() => setRequestInProgress(false));
  };

  const openAssignDevice = () => {
    if (!activeOrderId) return;
    setRightState('orderAssign');
    navigate(`/orders/${activeOrderId}/assign`);
  };

  const assignDeviceToOrder = (formValues, orderIdValue = null) => {
    const targetOrderId = orderIdValue || activeOrderId;
    if (!targetOrderId) return;
    setApiStatus('WORKING');
    API.post(`/orders/${targetOrderId}/assign`, formValues)
      .then(res => {
        updateOrderItem({ ...res.data });
        if (queryClient && res.data?.status) {
          queryClient.setQueryData(['order', targetOrderId], prevData => {
            if (!prevData) return prevData;
            return {
              ...prevData,
              status: res.data.status,
            };
          });
          queryClient.invalidateQueries({ queryKey: ['order', targetOrderId] });
        }
        setRightState('orderDetails');
        setActiveOrderId(res.data.id);
        setOrderStatus(res.data.status);
        setApiStatus('IDLE');
        navigate(`/orders/${res.data.id}`);
      })
      .catch(error => {
        console.log(error);
        setApiStatus('ERROR');
      });
  };

  const openAssignOrder = type => {
    setRightState('independentAssign');
    setCurrentInspectionType(type);
    navigate('/orders/independent-assign');
  };

  const openMergeOrder = type => {
    const inspectionId = activeOrderId;
    setRightState('independentMerge');
    setCurrentInspectionType(type);
    navigate(`/inspections/${inspectionId}/merge`);
  };

  const openEditPreviousInspection = ({ orderId: orderIdParam, orderType }) => {
    if (!orderIdParam || !orderType) return;
    const { previousOrderType } = getPreviousInspectionAttachConfig(orderType, intl);
    if (!previousOrderType) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'orderDetails.previousInspectionUpdatedError' }),
          duration: 5000,
        })
      );
      return;
    }
    setRightState('editPreviousInspectionAttach');
    setEditPreviousInspectionOrderId(orderIdParam);
    setEditPreviousInspectionType(orderType);
    navigate(`/orders/${orderIdParam}/previous-inspection/edit`);
  };

  const editPreviousInspection = option => {
    if (!editPreviousInspectionOrderId || !option?.id || isUpdatingPreviousInspection.current) return;
    isUpdatingPreviousInspection.current = true;
    setApiStatus('WORKING');
    API.patch(`/orders/${editPreviousInspectionOrderId}/previous-inspection`, { id: option.id })
      .then(() => {
        dispatch(
          showToast({
            type: 'success',
            title: intl.formatMessage({ id: 'toast.success.title' }),
            text: intl.formatMessage({ id: 'orderDetails.previousInspectionUpdatedSuccess' }),
            duration: 5000,
          })
        );
        setRightState('orderDetails');
        setActiveOrderId(editPreviousInspectionOrderId);
        setEditPreviousInspectionOrderId(null);
        setEditPreviousInspectionType(null);
        navigate(`/orders/${editPreviousInspectionOrderId}`);
      })
      .catch(() => {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: 'orderDetails.previousInspectionUpdatedError' }),
            duration: 5000,
          })
        );
      })
      .finally(() => {
        isUpdatingPreviousInspection.current = false;
        setApiStatus('IDLE');
      });
  };

  const toggleModal = modalName => setModals(prev => ({ ...prev, [modalName]: !prev[modalName] }));

  const mergeInspectionSelectionHandler = () => {
    let selectedInspectionIds = [];

    if (selectedOrders.size > 0) {
      selectedInspectionIds = Array.from(selectedOrders);
    } else if (activeOrderId !== null) {
      selectedInspectionIds = [activeOrderId];
    }

    if (selectedInspectionIds.length === 0) return;

    window.alert(selectedInspectionIds.join(','));
  };

  const callActionHandler = (action, val) => {
    let effectiveAction = action;
    let effectiveVal = val;
    if ((action === null || typeof action === 'object') && typeof val === 'string') {
      effectiveAction = val;
      effectiveVal = action;
    }

    switch (effectiveAction) {
      case 'edit':
      case 'openEditOrder':
        actionsMenuEditOrder();
        break;
      case 'delete':
      case 'openDeleteOrder':
        actionsMenuOrderDelete();
        break;
      case 'cancel':
      case 'openCancelOrder':
        actionsMenuOrderCancel();
        break;
      case 'generatePDF':
        actionsMenuGeneratePDF(
          effectiveVal?.withPictures,
          effectiveVal?.type,
          effectiveVal?.label,
          effectiveVal?.inspectionId
        );
        break;
      case 'export':
      case 'generatePhotos':
        actionsMenuGeneratePhotos();
        break;
      case 'archive':
      case 'archiveOrder':
        actionsMenuOrderArchive(null, 'archive');
        break;
      case 'unarchive':
      case 'unarchiveOrder':
        actionsMenuOrderArchive(null, 'unarchive');
        break;
      case 'merge-selection':
        mergeInspectionSelectionHandler();
        break;
      case 'merge':
        openMergeOrder(orderTypeValue);
        break;
      case 'checklist-form':
      case 'checklist': {
        const publicId = activeOrder?.public_id || (activeIndex > -1 ? data[activeIndex]?.public_id : null) || orderID;
        setRightState('checklist');
        navigate(`/orders/${publicId}/checklist`);
        break;
      }
      case 'extra-actions': {
        const publicId = activeOrder?.public_id || (activeIndex > -1 ? data[activeIndex]?.public_id : null) || orderID;
        setRightState('extra-actions');
        navigate(`/orders/${publicId}/extra-actions`);
        break;
      }
      case 'repair-budget-form':
        setRightState('repair-budget-form');
        navigate(`/orders/${orderID}/repair-budget`);
        break;
      case 'order-details': {
        const publicId = activeOrder?.public_id || (activeIndex > -1 ? data[activeIndex]?.public_id : null) || orderID;
        setRightState('orderDetails');
        if (activeSubMenu !== 'independent') {
          navigate(`/orders/${publicId}`);
        } else {
          navigate(`/inspections/${publicId}`);
        }
        break;
      }
    }
  };

  let orderID = selectedOrders.size === 1 ? selectedOrders.values().next().value : activeOrderId;
  const activeIndex = data.findIndex(order => order && String(order.id) === String(orderID));
  let inspectionIcon,
    orderStatusValue,
    orderTypeValue,
    flagsValue,
    isArchivedValue = false,
    isFromTheSameFranchisee = false;

  if (activeIndex !== -1) {
    inspectionIcon = <OrderIcon orderType={data[activeIndex].type} fontSize="calc(16px + 0.8vw)" />;
    orderStatusValue = data[activeIndex].status;
    orderTypeValue = data[activeIndex].type;
    flagsValue = data[activeIndex].flags;
    isArchivedValue = data[activeIndex].is_archived ?? false;
    isFromTheSameFranchisee = data[activeIndex]?.franchisee_id === franchiseeId || activeSubMenu === 'independent';
  }

  let multipleArchiveAllowed = false;
  if (selectedOrders.size > 1 && activeSubMenu !== 'independent') {
    const selectedItems = data.filter(item => item && selectedOrders.has(item.id));
    multipleArchiveAllowed = selectedItems.reduce((prev, current) => {
      const isNotArchivableStatus = ['FINISHED', 'REJECTED', 'CANCELED', 'WAITING'].includes(current.status);
      return prev && isNotArchivableStatus === false && current.is_archived === isArchivedValue;
    }, true);
  }

  const hasInitialized = useRef(false);

  // Initialize data and handle URL sync
  useEffect(() => {
    const init = async () => {
      const url = location.pathname;
      const orderIdMatch = url.match(/\/orders\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
      const inspectionIdMatch = url.match(/\/inspections\/([a-zA-Z0-9-]+)(?:\/|$)/);
      const isMergeRoute = url.includes('/merge');
      const inspectionId = inspectionIdMatch && inspectionIdMatch[1] !== 'error' ? inspectionIdMatch[1] : null;
      const isInspections = url.includes('/inspections');

      // If we are just selecting an order and we already have data, don't re-fetch
      if (orderIdMatch && data.length > 0) {
        const orderId = orderIdMatch[1];
        if (String(activeOrderId) !== String(orderId)) {
          setActiveOrderId(orderId);
          setRightState('orderDetails');
          // Update orderStatus if we find the order in our current data
          const order = data.find(o => o && String(o.id) === String(orderId));
          if (order) {
            setOrderStatus(order.status);
            setActiveOrder(order);
          }
        }
        return;
      }

      // If we are just selecting an inspection and we already have data, don't re-fetch
      if (inspectionId && data.length > 0) {
        if (String(activeOrderId) !== String(inspectionId)) {
          setActiveOrderId(inspectionId);
          if (isMergeRoute) {
            setRightState('independentMerge');
          } else {
            setRightState('orderDetails');
          }
          const inspection = data.find(o => o && String(o.id) === String(inspectionId));
          if (inspection) {
            setOrderStatus(inspection.status);
            setActiveOrder(inspection);
            setCurrentInspectionType(inspection.type);
          }
        }
        return;
      }

      // Deep link or initial load
      if (!hasInitialized.current || (data.length === 0 && !loadingOrders)) {
        hasInitialized.current = true;

        if (orderIdMatch) {
          setLoadingOrders(true);
          const orderId = orderIdMatch[1];
          try {
            // Use React Query fetch to benefit from cache
            const orderById = await queryClient.fetchQuery({
              queryKey: ['order', orderId],
              queryFn: () => getOrderById(orderId),
              staleTime: 3000,
            });

            let labelKey = orderById.is_archived
              ? 'archived'
              : orderStatusToStatusLabel.get(orderById.status) || 'waiting';

            const { data: ordersData } = await API.get(`/orders?status=${labelKey}&limit=${ITEMS_PER_PAGE}`);
            const isOrderInFirstPage = ordersData.some(o => String(o.id) === String(orderId));

            let finalData = ordersData;
            if (!isOrderInFirstPage) {
              finalData = [mapOrderToListView(orderById), ...ordersData];
            }

            setData(deduplicateOrders(finalData));
            setActiveOrderId(orderId);
            setActiveOrder(orderById);
            setOrderStatus(orderById.status);
            setRightState('orderDetails');
            setActiveSubMenu(labelKey);

            // Sync menu state
            setMenu(prevMenu => {
              const newMenu = [...prevMenu];
              const inspectionsIdx = newMenu.findIndex(m => m.key === 'inspections');
              if (inspectionsIdx !== -1) {
                const subMenuKey = statusLabelToSubMenu.get(labelKey);
                newMenu[inspectionsIdx].subMenu.forEach(sm => {
                  sm.active = sm.key === subMenuKey;
                  if (sm.collapseSubMenu) {
                    sm.collapseSubMenu.forEach(csm => {
                      csm.active = csm.key === labelKey;
                    });
                  }
                });
              }
              return newMenu;
            });
          } catch (err) {
            console.error('Failed to sync order from URL', err);
            handleOrdersRequestByStatus('waiting');
          } finally {
            setLoadingOrders(false);
          }
        } else if (inspectionId) {
          setLoadingOrders(true);
          try {
            // Fetch inspection using getOrderById
            const inspectionById = await queryClient.fetchQuery({
              queryKey: ['order', inspectionId],
              queryFn: () => getOrderById(inspectionId),
              staleTime: 3000,
            });

            const { data: inspectionsData } = await API.get(`/inspections?status=independent&limit=${ITEMS_PER_PAGE}`);
            const isInspectionInFirstPage = inspectionsData.some(o => String(o.id) === String(inspectionId));

            let finalData = inspectionsData;
            if (!isInspectionInFirstPage) {
              finalData = [inspectionById, ...inspectionsData];
            }

            setData(deduplicateOrders(finalData));
            setActiveOrderId(inspectionId);
            setActiveOrder(inspectionById);
            setOrderStatus(inspectionById.status);
            if (isMergeRoute) {
              setRightState('independentMerge');
              setCurrentInspectionType(inspectionById.type);
            } else {
              setRightState('orderDetails');
            }
            setActiveSubMenu('independent');

            // Sync menu state
            setMenu(prevMenu => {
              const newMenu = [...prevMenu];
              const inspectionsIdx = newMenu.findIndex(m => m.key === 'inspections');
              if (inspectionsIdx !== -1) {
                newMenu[inspectionsIdx].subMenu.forEach(sm => {
                  sm.active = sm.key === 'independent';
                });
              }
              return newMenu;
            });
          } catch (err) {
            console.error('Failed to sync inspection from URL', err);
            handleOrdersRequestByStatus('waiting');
          } finally {
            setLoadingOrders(false);
          }
        } else if (isInspections) {
          setDefaltOrderBy('independent');
          const res = await API.get(
            `/inspections?status=independent&limit=${ITEMS_PER_PAGE}&order_by=created_at&order_by_dir=desc`
          );
          setData(deduplicateOrders(res.data || []));
          setActiveSubMenu('independent');
        } else if (url === '/orders' || url.startsWith('/orders?')) {
          const params = new URLSearchParams(location.search);
          const tab = params.get('tab') || 'waiting';
          const defaultDir = setDefaltOrderBy(tab);
          setActiveSubMenu(tab);
          handleOrdersRequestByStatus(tab, defaultDir);

          setMenu(prevMenu => {
            const newMenu = [...prevMenu];
            const inspectionsIdx = newMenu.findIndex(m => m.key === 'inspections');
            if (inspectionsIdx !== -1) {
              const subMenuKey = statusLabelToSubMenu.get(tab) || 'active';
              newMenu[inspectionsIdx].subMenu.forEach(sm => {
                sm.active = sm.key === subMenuKey;
                if (sm.collapseSubMenu) {
                  sm.collapseSubMenu.forEach(csm => {
                    csm.active = csm.key === tab;
                  });
                }
              });
            }
            return newMenu;
          });
        }
      }
    };
    init();
  }, [location.pathname, location.search, handleOrdersRequestByStatus]);

  useEffect(() => {
    const match = location.pathname.match(/\/orders\/new\/([^/]+)/);
    if (match && match[1] && !['form', 'attach'].includes(match[1])) {
      if (match[1] !== newOrderType) setNewOrderType(match[1]);
    } else if (newOrderType && !location.pathname.startsWith('/orders/new/')) {
      setNewOrderType(null);
    }
  }, [location.pathname, newOrderType]);

  return (
    <div className="relative min-h-screen bg-[#EAEAEA] flex flex-col">
      <ApiContext.Provider value={{ status: apiStatus }}>
        <LeftDrawer
          menu={menu}
          handleMenuChange={handleMenuChange}
          handleSubMenuChange={handleSubMenuChange}
          handleCollapseSubMenuChange={handleCollapseSubMenuChange}
          isDisplayingSearchResults={isDisplayingSearchResults}
          subMenuToOpenByIndex={activeSubMenuIndex}
        />
        <Header
          headerSearchValue={headerSearchValue}
          handleHeaderSearch={handleHeaderSearch}
          userName={userName}
          activeSubMenu={currentPageByCurrentMenu.get(activeSubMenu)}
        />
        <main className="fixed top-[54px] left-[220px] w-[calc(100%_-_220px)] h-[calc(100%_-_54px)] bg-[#EAEAEA] flex flex-col overflow-hidden">
          <div className="w-full">
            <ActionsMenu
              orderByDir={orderByDir}
              changeOrderByDir={e => {
                setOrderByDir(e.value);
                const hasSearch =
                  searchStateRef.current.value ||
                  searchStateRef.current.tags ||
                  searchStateRef.current.type ||
                  searchStateRef.current.status;
                if (hasSearch) {
                  triggerSearch(e.value);
                } else {
                  refreshData(e.value);
                }
              }}
              orderId={orderID}
              orderType={orderTypeValue}
              orderStatus={orderStatusValue}
              actionHandler={callActionHandler}
              multipleArchiveAllowed={multipleArchiveAllowed}
              checkAll={actionsMenuCheckAllAction}
              newOrderAction={actionsMenuNewOrder}
              checked={actionsMenuChecked}
              disableCheckbox={['orderOptions', 'orderForm', 'orderAttach', 'orderEdit'].includes(rightState)}
              currentPage={currentPageByCurrentMenu.get(activeSubMenu)}
              isArchived={isArchivedValue}
              isFromTheSameFranchisee={isFromTheSameFranchisee}
              selectedOrder={data[activeIndex]}
              flags={flagsValue}
            />
          </div>
          <div className="flex flex-1 min-h-0 overflow-hidden">
            <div className="w-1/2 h-full overflow-hidden">
              {loadingOrders ? (
                <Loading />
              ) : data.length ? (
                <OrdersList
                  orderIdFromUrl={orderIdFromUrl}
                  orderShowDetailsAction={orderShowDetailsAction}
                  data={data}
                  checkOne={checkOneItemAction}
                  selectedCheckboxes={selectedOrders}
                  selectedItem={activeIndex}
                  loadMoreItems={loadMoreItems}
                  hasMoreItems={hasMoreItems}
                  showListRefresh={ordersChanged}
                  refreshData={refreshData}
                  fromPage={activeSubMenu}
                  isDisplayingSearchResults={isDisplayingSearchResults}
                />
              ) : (
                <Empty
                  header={<FormattedMessage id="empty.listHeader" />}
                  subHeader={<FormattedMessage id="empty.listSubHeader" />}
                />
              )}
            </div>
            <div className="w-1/2 h-full overflow-y-auto border-l border-[#EAEAEA] shadow-none z-10 pb-[20px]">
              <Routes>
                <Route
                  path="/"
                  element={
                    <Empty
                      header={<FormattedMessage id="empty.selectRequest" />}
                      subHeader={<FormattedMessage id="empty.createRequest" />}
                    />
                  }
                />
                <Route
                  path="cancel"
                  element={
                    activeIndex === -1 ? (
                      <Navigate to="/orders" replace />
                    ) : (
                      <OrderCancelPage
                        order={data[activeIndex]}
                        inspectionIcon={inspectionIcon}
                        cancelOrder={cancelOrder}
                        changePage={callActionHandler}
                      />
                    )
                  }
                />
                <Route
                  path="new/:orderType/form"
                  element={
                    <NewOrderFormRoute
                      previousOrderFile={previousOrderFile}
                      inspectionIcon={inspectionIcon}
                      updateOrderItem={updateOrderItem}
                      cancelNewOrder={cancelNewOrder}
                      previousOrderId={previousOrderId}
                      successfulOrderCreation={successfulOrderCreation}
                    />
                  }
                />
                <Route
                  path="new/:orderType/form/:previousId"
                  element={
                    <NewOrderFormRoute
                      previousOrderFile={previousOrderFile}
                      inspectionIcon={inspectionIcon}
                      updateOrderItem={updateOrderItem}
                      cancelNewOrder={cancelNewOrder}
                      previousOrderId={previousOrderId}
                      successfulOrderCreation={successfulOrderCreation}
                    />
                  }
                />
                <Route
                  path="new/:orderType/attach"
                  element={
                    <AttachWrapper
                      intl={intl}
                      orderAttachPanel={orderAttachPanel}
                      orderAttachFile={orderAttachFile}
                      continueWithoutAttachment={continueWithoutAttachment}
                    />
                  }
                />
                <Route
                  path=":orderId/previous-inspection/edit"
                  element={
                    <EditPreviousInspectionWrapper
                      editPreviousInspectionOrderId={editPreviousInspectionOrderId}
                      editPreviousInspectionType={editPreviousInspectionType}
                      intl={intl}
                      editPreviousInspection={editPreviousInspection}
                    />
                  }
                />
                <Route
                  path="new"
                  element={
                    <OrderOptionsList
                      handleOrderOptionSelect={handleOrderOptionSelect}
                      features={features}
                      withDescription={false}
                    />
                  }
                />
                <Route
                  path="independent-assign"
                  element={
                    <InspectionAttach
                      header={<FormattedMessage id="inspection.independent" />}
                      subHeader={<FormattedMessage id="inspectionAttach.order" />}
                      handleSearchConfirm={independentAttach}
                      searchPlaceholder={intl.formatMessage({ id: 'orderAttach.searchPanel' })}
                      apiUrl={`/orders?type=${currentInspectionType}&status=accepted_active&search=`}
                      onlySearch
                    />
                  }
                />
                <Route
                  path=":inspectionId/merge"
                  element={
                    <MergeWrapper
                      intl={intl}
                      independentMerge={independentMerge}
                      currentInspectionType={currentInspectionType}
                      setActiveOrderId={setActiveOrderId}
                      setCurrentInspectionType={setCurrentInspectionType}
                      activeOrder={activeOrder}
                    />
                  }
                />
                <Route
                  path="selection"
                  element={
                    <OrderSelectionMultiple
                      itemsAmount={selectedOrders.size}
                      actionHandler={callActionHandler}
                      orderId={orderID}
                      orderType={orderTypeValue}
                      orderStatus={orderStatusValue}
                      multipleArchiveAllowed={multipleArchiveAllowed}
                      currentPage={currentPageByCurrentMenu.get(activeSubMenu)}
                      flags={flagsValue}
                      isArchived={isArchivedValue}
                    />
                  }
                />
                <Route
                  path=":orderId/assign"
                  element={
                    <AssignInspector
                      assignDeviceToOrder={formValues => assignDeviceToOrder(formValues, orderID)}
                      cancelHandler={() => {
                        setRightState('orderDetails');
                        navigate(`/orders/${orderID}`);
                      }}
                    />
                  }
                />
                <Route path=":orderId/performing-inspector" element={<AddPerformingInspector orderId={orderID} />} />
                <Route
                  path=":orderId/extra-actions"
                  element={
                    <ExtraActions
                      changePage={callActionHandler}
                      orderData={activeIndex > -1 ? data[activeIndex] : null}
                      orderId={orderID}
                      orderType={orderTypeValue}
                      clientPanel={false}
                    />
                  }
                />
                <Route
                  path=":orderId/checklist"
                  element={
                    <ChecklistForm
                      changePage={callActionHandler}
                      orderId={orderID}
                      clientPanel={false}
                      actionHandler={callActionHandler}
                    />
                  }
                />
                <Route
                  path=":orderId/repair-budget"
                  element={
                    <RepairBudgetForm
                      changePage={callActionHandler}
                      orderId={orderID}
                      clientPanel={false}
                      actionHandler={callActionHandler}
                    />
                  }
                />
                <Route path="error" element={<ErrorPage />} />
                <Route
                  path=":orderId"
                  element={
                    <OrderDetails
                      notifyExistingOrderIdInUrl={setOrderIdFromUrl}
                      changePage={callActionHandler}
                      decrementBadge={decrementBadge}
                      updateOrderItem={updateOrderItem}
                      rejectOrder={rejectOrder}
                      openAssignDevice={openAssignDevice}
                      openAssignOrder={openAssignOrder}
                      openMergeOrder={openMergeOrder}
                      openEditPreviousInspection={openEditPreviousInspection}
                      orderStatus={orderStatusValue}
                      refreshData={refreshData}
                      showError={() => console.error('Inspection error')}
                      actionsMenuGeneratePDF={actionsMenuGeneratePDF}
                      currentPageByOrderStatus={currentPageByOrderStatus}
                      requestInProgress={requestInProgress}
                    />
                  }
                />
                <Route
                  path=":orderId/edit"
                  element={
                    <EditOrderFormPage
                      inspectionIcon={inspectionIcon}
                      updateOrderItem={updateOrderItem}
                      successfulOrderEdition={successfulOrderEdition}
                      cancelEditOrder={cancelEditOrder}
                      orderStatus={orderStatusValue}
                      clientPanel={false}
                    />
                  }
                />
                <Route
                  path="*"
                  element={
                    <Empty
                      header={<FormattedMessage id="empty.selectRequest" />}
                      subHeader={<FormattedMessage id="empty.createRequest" />}
                    />
                  }
                />
              </Routes>
            </div>
          </div>
        </main>
        {modals.extract && (
          <Modal onClose={() => toggleModal('extract')} extraStyles={{ overflowY: 'visible' }}>
            <OrdersReportsModal closeModal={() => toggleModal('extract')} />
          </Modal>
        )}
        <MarketingModal
          open={showMarketingModal}
          onClose={handleCloseMarketingModal}
          onBannerClick={getDivergencesPanelLink}
          typeBanner="divergences"
        />
      </ApiContext.Provider>
    </div>
  );
};

FranchiseeApp.propTypes = {
  userName: PropTypes.string.isRequired,
  features: PropTypes.array.isRequired,
  intl: PropTypes.object.isRequired,
  ordersChanged: PropTypes.bool.isRequired,
  ordersUpdated: PropTypes.func.isRequired,
  franchiseeId: PropTypes.number.isRequired,
};

export default FranchiseeApp;
